import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-access-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule],
  templateUrl: './app-access-sidebar.component.html',
  styleUrl: './app-access-sidebar.component.scss',
})
export class AppAccessSidebarComponent {
  @Input() items: MenuItem[] = [];

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  public pinnedMenuKey = signal<string | null>(null);
  public hoveredMenuKey = signal<string | null>(null);
  public openedChildMenuKey = signal<string | null>(null);

  protected expanded = true;

  private readonly submenuHoverDelay = 350;
  private submenuHoverTimeout: ReturnType<typeof setTimeout> | null = null;

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const clickedInsideSidebar = this.elementRef.nativeElement.contains(
      event.target as Node,
    );

    if (!clickedInsideSidebar) {
      this.closeSubmenu();
    }
  }

  protected toggleSubmenu(item: MenuItem): void {
    const key = String(item.label);

    this.pinnedMenuKey.update((current) => {
      const next = current === key ? null : key;

      this.hoveredMenuKey.set(null);

      return next;
    });
  }

  protected openSubmenuOnHover(item: MenuItem): void {
    if (this.pinnedMenuKey()) return;

    this.clearSubmenuHoverTimeout();

    const key = String(item.label);
    this.submenuHoverTimeout = setTimeout(() => {
      this.hoveredMenuKey.set(key);
    }, this.submenuHoverDelay);
  }

  protected closeSubmenuOnHover(): void {
    this.clearSubmenuHoverTimeout();

    if (this.pinnedMenuKey()) return;

    this.hoveredMenuKey.set(null);
    this.openedChildMenuKey.set(null);
  }

  private clearSubmenuHoverTimeout(): void {
    if (this.submenuHoverTimeout) {
      clearTimeout(this.submenuHoverTimeout);
      this.submenuHoverTimeout = null;
    }
  }

  protected isSubmenuOpen(item: MenuItem): boolean {
    const key = String(item.label);

    return this.pinnedMenuKey() === key || this.hoveredMenuKey() === key;
  }

  protected toggleChildSubmenu(item: MenuItem, event: Event): void {
    event.stopPropagation();

    const key = String(item.label);

    this.openedChildMenuKey.update((current) => (current === key ? null : key));
  }

  protected isChildSubmenuOpen(item: MenuItem): boolean {
    return this.openedChildMenuKey() === String(item.label);
  }

  protected closeSubmenu(): void {
    this.pinnedMenuKey.set(null);
    this.hoveredMenuKey.set(null);
  }

  protected onCommand(item: MenuItem, event: Event): void {
    item.command?.({ originalEvent: event, item });
  }

  protected hasChildren(item: MenuItem): boolean {
    return !!item.items?.some((child) => child.visible !== false);
  }

  protected hasGrandChildren(item: MenuItem): boolean {
    return !!item.items?.some((child) => child.visible !== false);
  }
}
